import { useCallback } from 'react';
import variableTypes from './variable-types';
import updateVariableLookup from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/UpdateVariable/variable-types';
import { useTranslation } from 'react-i18next';
import { useAutomationsSelector } from '__pages/AutomationEngine/store/react';
import { getMetaData } from 'pages/AutomationEngine/store/selectors';
import type {
  AutomationMetaData,
  AutomationVariableDataType,
  Field,
  KizenFieldType,
  VariableCompatibility,
  VariableDefinitionType,
  VariableUpdateType,
} from 'pages/AutomationEngine/types';
import {
  type DisabledTypes,
  type DisabledDetailsTypes,
} from './VariableWizardContext';
import { VariableNameAndType } from './types';
import FieldService from 'services/FieldService';
import { FIELD_TYPES } from 'utility/constants';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { getObjectKeys } from 'pages/AutomationEngine/ts-helpers';

type FieldLike = {
  fieldType: KizenFieldType;
  properties?: Pick<Field['properties'], 'allowMultiple'>;
  relation?: Pick<Field['relation'], 'cardinality' | 'relatedObject'>;
};
type VariableLike = Pick<
  VariableNameAndType,
  'dataType' | 'isArray' | 'dataSubtype'
>;

export const defaultDisabledTypes: Record<VariableDefinitionType, boolean> = {
  static: true,
  logged_activity_trigger: true,
  activity_past_due_trigger: true,
  field_value: true,
  trigger_time: true,
  webhook_trigger: true,
};

const defaultDisabledDetialsTypes = {
  static: [],
  logged_activity_trigger: [],
  activity_past_due_trigger: [],
  field_value: [],
  trigger_time: [],
  webhook_trigger: [],
};

const isVariableCompatibleBySubtype = (
  sourceVariable: VariableLike,
  targetVariable: VariableLike
) => {
  switch (sourceVariable.dataType) {
    case 'entity': {
      if (targetVariable.dataType !== 'entity') {
        return true;
      }

      return (
        typeof sourceVariable?.dataSubtype === 'object' &&
        sourceVariable?.dataSubtype?.value === targetVariable.dataSubtype?.value
      );
    }

    default:
      return true;
  }
};

const isFieldCompatibleBySubtype = (
  field: FieldLike,
  variable: VariableLike
) => {
  switch (variable.dataType) {
    case 'entity': {
      if (field.fieldType !== FIELD_TYPES.Relationship.type) {
        return true;
      }

      return (
        typeof variable?.dataSubtype === 'object' &&
        variable?.dataSubtype?.value === field.relation?.relatedObject
      );
    }

    default:
      return true;
  }
};

export const useCheckVariableCompatibility = () => {
  const metaData: AutomationMetaData = useAutomationsSelector(getMetaData);

  const checkVariableFromFieldCompatibility = useCallback(
    (field: FieldLike | undefined, variable: VariableLike | undefined) => {
      if (field && variable) {
        const isCompatible =
          isFieldCompatibleBySubtype(field, variable) &&
          metaData?.variables?.variable_from_field?.[variable.dataType]?.[
            variable.isArray ? 'array' : 'single'
          ]?.[field.fieldType];

        if (!isCompatible) {
          return false;
        }

        if (isCompatible.only_allow_multiple) {
          return FieldService.getAllowMultiple(field);
        }

        if (isCompatible.only_allow_single) {
          return !FieldService.getAllowMultiple(field);
        }

        return true;
      }
      return false;
    },
    [metaData]
  );

  const getFieldFromVariableCompatibility = useCallback(
    (field: FieldLike | undefined): VariableCompatibility => {
      if (!field) {
        return {};
      }

      const rawCompatibility =
        metaData.variables?.field_from_variable?.[field.fieldType];

      const compatibility: VariableCompatibility = {};

      const dataTypes = getObjectKeys(rawCompatibility);

      dataTypes.forEach((dataType) => {
        const cardinalities = getObjectKeys(rawCompatibility?.[dataType]);
        if (cardinalities?.length) {
          cardinalities.forEach((cardinality) => {
            const isMultiValueField = FieldService.getAllowMultiple(field);
            const allowed = rawCompatibility?.[dataType]?.[cardinality];

            if (
              !allowed ||
              (allowed.only_allow_multiple && !isMultiValueField) ||
              (allowed.only_allow_single && isMultiValueField)
            ) {
              return;
            }

            compatibility[dataType] = {
              ...compatibility[dataType],
              [cardinality]: allowed,
            };
          });
        }
      });

      return compatibility;
    },
    [metaData]
  );

  const checkFieldFromVariableCompatibility = useCallback(
    (field: FieldLike | undefined, variable: VariableLike | undefined) => {
      if (field && variable) {
        return (
          !!isFieldCompatibleBySubtype(field, variable) &&
          getFieldFromVariableCompatibility(field)[variable.dataType]?.[
            variable.isArray ? 'array' : 'single'
          ]
        );
      }
      return false;
    },
    [getFieldFromVariableCompatibility]
  );

  const getVariableVariableCompatibility = useCallback(
    (targetVariable: VariableLike | undefined): VariableCompatibility => {
      if (!targetVariable) {
        return {};
      }

      return (
        metaData.variables?.variable_from_variable?.[targetVariable.dataType][
          targetVariable.isArray ? 'array' : 'single'
        ] ?? {}
      );
    },
    [metaData]
  );

  const checkVariableVariableCompatibility = useCallback(
    (
      sourceVariable: VariableLike | undefined,
      targetVariable: VariableLike | undefined
    ) => {
      if (!sourceVariable || !targetVariable) {
        return false;
      }

      if (!isVariableCompatibleBySubtype(sourceVariable, targetVariable)) {
        return false;
      }

      return !!getVariableVariableCompatibility(targetVariable)[
        sourceVariable.dataType
      ]?.[sourceVariable.isArray ? 'array' : 'single'];
    },
    [getVariableVariableCompatibility]
  );

  const convertFieldTypeToVariableDataType = useCallback(
    (fieldType: KizenFieldType): AutomationVariableDataType => {
      return (
        metaData?.variables?.direct_field_to_variable?.[fieldType] ??
        AUTOMATION_VARIABLE_DATA_TYPES.string
      );
    },
    [metaData]
  );

  return {
    metaData,
    checkVariableFromFieldCompatibility,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    checkFieldFromVariableCompatibility,
    getFieldFromVariableCompatibility,
    convertFieldTypeToVariableDataType,
  };
};

export type CheckVariableFromFieldCompatibilityFN = ReturnType<
  typeof useCheckVariableCompatibility
>['checkVariableFromFieldCompatibility'];
export type CheckVariableVariableCompatibilityFN = ReturnType<
  typeof useCheckVariableCompatibility
>['checkVariableVariableCompatibility'];
export type GetVariableVariableCompatibilityFN = ReturnType<
  typeof useCheckVariableCompatibility
>['getVariableVariableCompatibility'];
export type CheckFieldFromVariableCompatibilityFN = ReturnType<
  typeof useCheckVariableCompatibility
>['checkFieldFromVariableCompatibility'];

export const useVariableMetaData = (
  typeLookUp:
    | Record<VariableDefinitionType, any>
    | Record<VariableUpdateType, any>,
  isInitalize: boolean = true
) => {
  const { t } = useTranslation();

  const {
    metaData,
    checkVariableFromFieldCompatibility,
    getFieldFromVariableCompatibility,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    checkFieldFromVariableCompatibility,
  } = useCheckVariableCompatibility();

  const lookup = isInitalize ? variableTypes : updateVariableLookup;

  const getDisabledTypes = useCallback(
    (variable: VariableNameAndType): DisabledTypes => {
      return (Object.keys(typeLookUp) as Array<keyof typeof typeLookUp>).reduce(
        (previous, key) => {
          let isDisabled = false;

          const getFilteredValueSourceOptions =
            lookup[key].getFilteredValueSourceOptions;

          if (getFilteredValueSourceOptions) {
            if (
              [
                'logged_activity_trigger',
                'activity_past_due_trigger',
                'trigger_time',
              ].includes(key)
            ) {
              isDisabled = !getFilteredValueSourceOptions(
                checkVariableFromFieldCompatibility,
                variable,
                t
              ).length;
            }
          }
          return { ...previous, [key]: isDisabled };
        },
        defaultDisabledTypes
      );
    },
    [checkVariableFromFieldCompatibility, lookup, t, typeLookUp]
  );

  const getCompatibleSources = useCallback(
    (variable: VariableNameAndType): DisabledDetailsTypes => {
      return (Object.keys(typeLookUp) as Array<keyof typeof typeLookUp>).reduce(
        (previous, key) => {
          let allowed: string[] = [];

          const getFilteredValueSourceOptionDetails =
            lookup[key].getFilteredValueSourceOptionDetails;

          if (getFilteredValueSourceOptionDetails) {
            if (
              [
                'logged_activity_trigger',
                'activity_past_due_trigger',
                'trigger_time',
              ].includes(key)
            ) {
              allowed = getFilteredValueSourceOptionDetails(
                checkVariableFromFieldCompatibility,
                variable,
                t
              );
            }
          }

          return { ...previous, [key]: allowed };
        },
        defaultDisabledDetialsTypes
      );
    },
    [checkVariableFromFieldCompatibility, lookup, t, typeLookUp]
  );

  return {
    metaData,
    checkVariableFromFieldCompatibility,
    getFieldFromVariableCompatibility,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    checkFieldFromVariableCompatibility,
    getDisabledTypes,
    getCompatibleSources,
  };
};
